import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
import Documents from "@/pages/Documents";
import Profile from "@/pages/Profile";
import Roster from "@/pages/Roster";
import Finance from "@/pages/Finance";
import Admin from "@/pages/Admin";
import DepthChart from "@/pages/DepthChart";
import PendingApproval from "@/pages/PendingApproval";
import CredentialValidation from "@/pages/CredentialValidation";
import SomosFerro from "@/pages/SomosFerro";
import FerroSection from "@/pages/FerroSection";
import FerroPostDetail from "@/pages/FerroPostDetail";
import AdminAuditLog from "@/pages/AdminAuditLog";
import Attendance from "@/pages/Attendance";
import Fixture from "@/pages/Fixture";

function Protected({ children }) {
  const { user, loading, refresh, onboardingCompleted } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (user?.role !== "pending") return;
    const id = setInterval(() => refresh(), 30000);
    return () => clearInterval(id);
  }, [user, refresh]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="flex gap-1">
          <span className="loading-dot" />
          <span className="loading-dot" style={{ animationDelay: ".2s" }} />
          <span className="loading-dot" style={{ animationDelay: ".4s" }} />
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "pending") return <PendingApproval />;
  // Force first-time onboarding: lock to /profile until rut+emergency_phone present
  if (!onboardingCompleted && location.pathname !== "/profile") {
    return <Navigate to="/profile" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback when session_id is in the URL fragment
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/credential/:userId" element={<CredentialValidation />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/somos-ferro" element={<Protected><SomosFerro /></Protected>} />
      <Route path="/somos-ferro/:sectionId" element={<Protected><FerroSection /></Protected>} />
      <Route path="/somos-ferro/:sectionId/:postId" element={<Protected><FerroPostDetail /></Protected>} />
      <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/roster" element={<Protected><Roster /></Protected>} />
      <Route path="/depth" element={<Protected><DepthChart /></Protected>} />
      <Route path="/dues" element={<Protected><Finance /></Protected>} />
      <Route path="/finance" element={<Protected><Finance /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/admin/audit" element={<Protected><AdminAuditLog /></Protected>} />
      <Route path="/attendance" element={<Protected><Attendance /></Protected>} />
      <Route path="/fixture" element={<Protected><Fixture /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
